import moment from "moment";
import { v4 as uuidv4 } from "uuid";

/**
 * Calculate runtime status of a time-bounded entry based on date range.
 * @param {string} startDate - ISO date string
 * @param {string} endDate - ISO date string
 * @returns {"Active"|"Scheduled"|"Concluded"|"Draft"}
 */
export const calculateRuntimeStatus = (startDate, endDate) => {
  if (!startDate || !endDate) return "Draft";
  const now = moment();
  const start = moment(startDate);
  const end = moment(endDate);

  if (now.isBetween(start, end, undefined, "[]")) return "Active";
  if (start.isAfter(now)) return "Scheduled";
  return "Concluded";
};

/**
 * Status color mapping for Tag component from pepsico-ds.
 */
export const statusColorMap = {
  Active: "Green-600",
  Scheduled: "Blue-400",
  Concluded: "Gray-500",
  Draft: "Gray-100",
};

/**
 * Validate that end date is after start date.
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string|null} error message or null
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = moment(startDate);
  const end = moment(endDate);
  if (end.isBefore(start)) {
    return "End date must not be before start date";
  }
  return null;
};

/**
 * Validate that a date is not in the past.
 * @param {string} dateStr - ISO date string
 * @returns {string|null} error message or null
 */
export const validateNotInPast = (dateStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr);
  const now = moment();
  if (date.isBefore(now)) {
    return "Date cannot be in the past";
  }
  return null;
};

/**
 * Check if a date range overlaps with any existing entries.
 * @param {string} startDate - ISO string
 * @param {string} endDate - ISO string
 * @param {Array} existingEntries - array with effective_start_date and effective_end_date
 * @param {string|null} excludeId - id to exclude from overlap check (for editing)
 * @returns {string|null} error message or null
 */
export const validateNoOverlap = (
  startDate,
  endDate,
  existingEntries,
  excludeId = null
) => {
  if (!startDate || !endDate || !existingEntries?.length) return null;
  const newStart = moment(startDate);
  const newEnd = moment(endDate);

  for (const entry of existingEntries) {
    if (excludeId && entry.id === excludeId) continue;
    const existingStart = moment(entry.effective_start_date);
    const existingEnd = moment(entry.effective_end_date);
    // Two ranges overlap if one starts before the other ends and vice versa
    if (newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart)) {
      return "Date range overlaps with an existing entry";
    }
  }
  return null;
};

/**
 * Return field-level validation errors for a booster row.
 */
export const getBoosterValidationErrors = (booster) => {
  const errors = {};
  if (!booster?.name?.trim()) {
    errors.name = "Booster name is required";
  }
  if (
    booster?.value === "" ||
    booster?.value === null ||
    booster?.value === undefined
  ) {
    errors.value = "Booster value is required";
  }
  if (!booster?.effective_start_date) {
    errors.startDate = "Start date is required";
  } else {
    const pastError = validateNotInPast(booster.effective_start_date);
    if (pastError) errors.startDate = "Start date cannot be in the past";
  }
  if (!booster?.effective_end_date) {
    errors.endDate = "End date is required";
  }

  const dateRangeError = validateDateRange(
    booster?.effective_start_date,
    booster?.effective_end_date
  );
  if (dateRangeError) errors.dateRange = dateRangeError;

  return errors;
};

/**
 * Apply the Time-Bound Booster filters entirely in the browser.
 */
export const filterBoosters = (boosters = [], filters = {}) => {
  const normalizedName = (filters.name || "").trim().toLowerCase();
  return boosters.filter((booster) => {
    const matchesName =
      !normalizedName || (booster.name || "").toLowerCase().includes(normalizedName);
    const matchesType = !filters.type || booster._boosterType === filters.type;
    const matchesStartDate =
      !filters.startDate ||
      (booster.effective_start_date &&
        moment(booster.effective_start_date).isSame(filters.startDate, "day"));
    const matchesEndDate =
      !filters.endDate ||
      (booster.effective_end_date &&
        moment(booster.effective_end_date).isSame(filters.endDate, "day"));
    const matchesStatus =
      !filters.status ||
      calculateRuntimeStatus(
        booster.effective_start_date,
        booster.effective_end_date
      ) === filters.status;

    return (
      matchesName &&
      matchesType &&
      matchesStartDate &&
      matchesEndDate &&
      matchesStatus
    );
  });
};

/**
 * Apply filters to both default tier rows and time-bounded tier overrides.
 */
export const filterTierMultipliers = (multipliers = [], filters = {}) => {
  return multipliers.filter((multiplier) => {
    const tierIds = Array.isArray(multiplier.tier_id)
      ? multiplier.tier_id
      : multiplier.tier_id
        ? [multiplier.tier_id]
        : multiplier.id
          ? [multiplier.id]
          : [];
    const matchesTier = !filters.tierId || tierIds.includes(filters.tierId);
    const matchesStartDate =
      !filters.startDate ||
      (multiplier.effective_start_date &&
        moment(multiplier.effective_start_date).isSame(filters.startDate, "day"));
    const matchesEndDate =
      !filters.endDate ||
      (multiplier.effective_end_date &&
        moment(multiplier.effective_end_date).isSame(filters.endDate, "day"));
    const matchesStatus =
      !filters.status ||
      calculateRuntimeStatus(
        multiplier.effective_start_date,
        multiplier.effective_end_date
      ) === filters.status;

    return matchesTier && matchesStartDate && matchesEndDate && matchesStatus;
  });
};

/**
 * Apply filters to default or time-bounded product and product-group rows.
 */
export const filterProductMultipliers = (multipliers = [], filters = {}) => {
  return multipliers.filter((multiplier) => {
    const matchesType = !filters.type || multiplier.level === filters.type;
    const matchesProduct =
      !filters.productId ||
      (multiplier.product_ids || []).includes(filters.productId);
    const matchesProductGroup =
      !filters.productGroupId ||
      (multiplier.product_group_ids || []).includes(filters.productGroupId);
    const matchesStartDate =
      !filters.startDate ||
      (multiplier.effective_start_date &&
        moment(multiplier.effective_start_date).isSame(filters.startDate, "day"));
    const matchesEndDate =
      !filters.endDate ||
      (multiplier.effective_end_date &&
        moment(multiplier.effective_end_date).isSame(filters.endDate, "day"));
    const matchesStatus =
      !filters.status ||
      calculateRuntimeStatus(
        multiplier.effective_start_date,
        multiplier.effective_end_date
      ) === filters.status;

    return (
      matchesType &&
      matchesProduct &&
      matchesProductGroup &&
      matchesStartDate &&
      matchesEndDate &&
      matchesStatus
    );
  });
};

/**
 * Return field-level validation errors for a product multiplier row.
 */
export const getProductMultiplierValidationErrors = (multiplier) => {
  const errors = {};
  const field =
    multiplier?.level === "product_group" ? "product_group_ids" : "product_ids";
  const ids = multiplier?.[field];
  if (!ids?.length) {
    errors.selection =
      multiplier?.level === "product_group"
        ? "At least one product group is required"
        : "At least one product is required";
  }
  if (
    multiplier?.value === "" ||
    multiplier?.value === null ||
    multiplier?.value === undefined
  ) {
    errors.value = "Multiplier value is required";
  }

  if (multiplier?.type === "time_bounded") {
    if (!multiplier?.effective_start_date) {
      errors.startDate = "Start date is required";
    } else {
      const pastError = validateNotInPast(multiplier.effective_start_date);
      if (pastError) errors.startDate = "Start date cannot be in the past";
    }
    if (!multiplier?.effective_end_date) {
      errors.endDate = "End date is required";
    }
    const dateRangeError = validateDateRange(
      multiplier?.effective_start_date,
      multiplier?.effective_end_date
    );
    if (dateRangeError) errors.dateRange = dateRangeError;
  }

  return errors;
};

/**
 * Return field-level validation errors for a tier multiplier row (time-bounded).
 */
export const getTierMultiplierValidationErrors = (multiplier) => {
  const errors = {};
  if (!multiplier?.tier_id?.length) {
    errors.tierSelection = "At least one tier is required";
  }
  if (
    multiplier?.value === "" ||
    multiplier?.value === null ||
    multiplier?.value === undefined
  ) {
    errors.value = "Multiplier value is required";
  }
  if (!multiplier?.effective_start_date) {
    errors.startDate = "Start date is required";
  } else {
    const pastError = validateNotInPast(multiplier.effective_start_date);
    if (pastError) errors.startDate = "Start date cannot be in the past";
  }
  if (!multiplier?.effective_end_date) {
    errors.endDate = "End date is required";
  }
  const dateRangeError = validateDateRange(
    multiplier?.effective_start_date,
    multiplier?.effective_end_date
  );
  if (dateRangeError) errors.dateRange = dateRangeError;

  return errors;
};

/**
 * Generate a new UUID for entries.
 */
export const generateId = () => uuidv4();

/**
 * Format a date for display.
 * @param {string} dateStr - ISO string
 * @returns {string} formatted date
 */
export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "None";
  return moment(dateStr).format("MM/DD/YYYY");
};

/**
 * Create a new empty booster entry.
 * @param {string} type - "Multiplier" or "Flat points"
 */
export const createEmptyBooster = (type = "Multiplier") => ({
  id: generateId(),
  name: "",
  type,
  value: "",
  include_segments: [],
  exclude_segments: [],
  effective_start_date: "",
  effective_end_date: "",
});

/**
 * Create a new empty tier multiplier entry (for time-bounded).
 */
export const createEmptyTierMultiplier = () => ({
  id: generateId(),
  tier_id: [],
  value: "",
  effective_start_date: "",
  effective_end_date: "",
});

/**
 * Create a new empty product multiplier entry.
 * @param {string} type - "default" or "time_bounded"
 * @param {string} level - "product" or "product_group"
 */
export const createEmptyProductMultiplier = (
  type = "default",
  level = "product_group"
) => ({
  id: generateId(),
  level,
  type,
  product_ids: [],
  product_group_ids: [],
  value: "",
  ...(type === "time_bounded"
    ? { effective_start_date: "", effective_end_date: "" }
    : {}),
});
